import React, { useMemo } from 'react';

// Groupes toujours visibles dans la timeline
const BASE_GROUPS = [
  { key: 'session', label: 'Session' },
  { key: 'memory', label: 'Mémoire' },
  { key: 'prompt', label: 'Contexte' },
  { key: 'llm', label: 'Génération' },
  { key: 'saving', label: 'Sauvegarde' },
];

// Groupes conditionnels (apparaissent quand déclenchés)
const CONDITIONAL_GROUPS = {
  saving_message: { label: 'Enregistrement', after: 'session' },
  history: { label: 'Historique', after: 'saving_message' },
  help: { label: 'Guide ITRIIA', after: 'session', color: '#10b981' },
  expertise: { label: 'Expertise', after: 'memory' },
  expertise_bases: { label: 'Bases d\'expertise', after: 'memory' },
  tools: { label: 'Outils', after: 'llm' },
  enrichissement: { label: 'Enrichissement', after: 'tools' },
  // Forçage serveur des boutons « publier sur le site » (structuration de l'article inline).
  // after:'enrichissement' absent (cas Opus pur sans outil) → inséré avant 'saving' (fallback).
  publication: { label: 'Publication', after: 'enrichissement', color: '#8b5cf6' },
};

const StreamStatus = ({ currentStep, isStreaming, completedGroups = [] }) => {
  // Visible quand streaming OU quand il y a des groupes complétés (état final reste visible)
  if (!isStreaming && completedGroups.length === 0) return null;
  if (!currentStep && completedGroups.length === 0) return null;

  const activeGroup = currentStep?.group;

  // Construire la liste des groupes visibles dynamiquement
  const visibleGroups = useMemo(() => {
    const allSeen = new Set([...completedGroups, activeGroup].filter(Boolean));

    const groups = [...BASE_GROUPS];

    // Insérer les groupes conditionnels s'ils ont été déclenchés
    for (const [key, config] of Object.entries(CONDITIONAL_GROUPS)) {
      if (allSeen.has(key)) {
        const entry = { key, label: config.label };
        if (config.color) entry.color = config.color;
        const afterIndex = groups.findIndex(g => g.key === config.after);
        if (afterIndex !== -1) {
          groups.splice(afterIndex + 1, 0, entry);
        } else {
          const savingIdx = groups.findIndex(g => g.key === 'saving');
          groups.splice(savingIdx, 0, entry);
        }
      }
    }

    return groups;
  }, [activeGroup, completedGroups]);

  // Dériver l'état de chaque dot
  const getGroupState = (groupKey) => {
    if (completedGroups.includes(groupKey)) return 'completed';
    if (activeGroup === groupKey) return 'active';
    return 'pending';
  };

  // Label dynamique (vient du backend)
  const activeLabel = currentStep?.label || '';

  return (
    <div style={styles.container}>
      <style>{KEYFRAMES}</style>

      <div style={styles.timeline}>
        {visibleGroups.map((group, i) => {
          const state = getGroupState(group.key);
          const isLast = i === visibleGroups.length - 1;

          return (
            <React.Fragment key={group.key}>
              {/* Dot */}
              <div style={styles.dotWrapper}>
                <div
                  style={{
                    ...styles.dot,
                    ...(state === 'completed' ? styles.dotCompleted : {}),
                    ...(state === 'active' ? styles.dotActive : {}),
                    ...(state === 'pending' ? styles.dotPending : {}),
                    ...(group.color && state === 'completed' ? { background: group.color } : {}),
                    ...(group.color && state === 'active' ? { background: group.color, boxShadow: `0 0 6px 2px ${group.color}60` } : {}),
                  }}
                  title={group.label}
                >
                  {state === 'completed' && (
                    <svg width="8" height="8" viewBox="0 0 12 12" style={{ animation: 'check-pop 0.3s ease-out' }}>
                      <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                {/* Label sous le dot actif */}
                {state === 'active' && activeLabel && (
                  <div style={styles.labelContainer}>
                    <span style={styles.labelText}>{activeLabel}</span>
                  </div>
                )}
              </div>

              {/* Connecteur entre dots */}
              {!isLast && (
                <div
                  style={{
                    ...styles.connector,
                    ...(state === 'completed'
                      ? styles.connectorCompleted
                      : state === 'active'
                        ? styles.connectorActive
                        : styles.connectorPending
                    ),
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const KEYFRAMES = `
  @keyframes orb-pulse {
    0%, 100% {
      box-shadow: 0 0 4px 1px rgba(29, 78, 216, 0.6), 0 0 8px 2px rgba(124, 58, 237, 0.3);
      transform: scale(1);
    }
    50% {
      box-shadow: 0 0 8px 3px rgba(124, 58, 237, 0.7), 0 0 16px 4px rgba(29, 78, 216, 0.4);
      transform: scale(1.3);
    }
  }
  @keyframes check-pop {
    0% { transform: scale(0); }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const styles = {
  container: {
    maxWidth: '450px',
    marginBottom: '12px',
    animation: 'fade-in 0.3s ease',
  },
  timeline: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0',
    paddingTop: '4px',
  },
  dotWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    minWidth: '12px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    flexShrink: 0,
  },
  dotPending: {
    width: '8px',
    height: '8px',
    background: '#3a3a4a',
    border: '1px solid #555',
  },
  dotActive: {
    width: '12px',
    height: '12px',
    background: '#1D4ED8',
    border: 'none',
    animation: 'orb-pulse 1.8s ease-in-out infinite',
  },
  dotCompleted: {
    width: '10px',
    height: '10px',
    background: '#22c55e',
    border: 'none',
  },
  connector: {
    height: '2px',
    flex: 1,
    minWidth: '8px',
    alignSelf: 'center',
    position: 'relative',
    top: '1px',
    transition: 'background 0.3s ease',
  },
  connectorCompleted: {
    background: '#22c55e',
  },
  connectorActive: {
    background: 'linear-gradient(to right, #22c55e, #1D4ED8)',
  },
  connectorPending: {
    background: '#3a3a4a',
  },
  labelContainer: {
    position: 'absolute',
    top: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    animation: 'fade-in 0.2s ease',
  },
  labelText: {
    fontSize: '11px',
    color: '#e0e0e0',
    fontWeight: '500',
    lineHeight: '1.2',
  },
};

export default StreamStatus;
